import * as React from "react";
import { withStores } from "stores";
import { normalizeOptionsData } from "src/components/flexFramework/FlexTemplateNode";
import { FlexModuleModelStore } from "stores/FlexModuleModelStore";

import { useDialog } from "@shared-ui/dialog-context";

import {
  PropertyLocationHighlights,
  PropertyMap,
  PropertySummaryHeader,
  PropertySummaryMapTrigger,
  PropertySummaryReviews,
  PropertyRelevantAmenities,
  PropertySummaryAmenities,
} from "@shared-ui/lodging-property-details";

import { PropertyFilteredReviewsDialog } from "components/shared/PropertyFilteredReviewsDialog/PropertyFilteredReviewsDialog";
import { PropertyStickyBookBar } from "@shared-ui/lodging-property-offers";

import { PropertyBexApiWrapper } from "components/shared/BexApiWrapper/PropertyBexApiWrapper";

import { ComponentProps, FTNodeOptions } from "typings/flexFramework/FlexComponent";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkFigureAspectRatioType } from "uitk-react-images";

import { ViewMedium, Viewport, ViewSmall } from "@shared-ui/viewport-context";

const WrappedSummaryHeader = PropertyBexApiWrapper(PropertySummaryHeader);
const WrappedStickyBookBar = PropertyBexApiWrapper(PropertyStickyBookBar);
const WrappedSummaryMapTrigger = PropertyBexApiWrapper(PropertySummaryMapTrigger);
const WrappedPropertyMap = PropertyBexApiWrapper(PropertyMap);
const WrappedPropertyReviews = PropertyBexApiWrapper(PropertySummaryReviews);
const WrappedPropertyLocationHighlights = PropertyBexApiWrapper(PropertyLocationHighlights);
const WrapperPropertyRelevantAmenities = PropertyBexApiWrapper(PropertyRelevantAmenities);
const WrapperPropertySummaryAmenities = PropertyBexApiWrapper(PropertySummaryAmenities);

interface PropertySummaryOptions extends FTNodeOptions {
  hideCtaButton?: boolean;
}

export interface PropertySummaryProps extends ComponentProps {
  flexModuleModelStore: FlexModuleModelStore;
}

const PropertySummaryLocationHighlights = withStores("flexModuleModelStore")((props: PropertySummaryProps) => {
  const { context, flexTemplateNode, flexModuleModelStore } = props;

  const id = flexTemplateNode?.id || "";
  const [, reviewsDialogActions, ReviewsDialogComponent] = useDialog(`summary-reviews-${id}`);
  const [, mapDialogActions, MapDialogComponent] = useDialog(`summary-map-${id}`);

  const isHotelscomPage = context.site.brand === "hotels";
  const additionalPropertyReviewsProps = { ...(isHotelscomPage && { highestAllowedRating: 10 }) };

  if (!flexTemplateNode) {
    return null;
  }

  const model = flexModuleModelStore?.getModel(id);
  const options = normalizeOptionsData(flexTemplateNode, model) as PropertySummaryOptions;
  const hideCtaButton = options?.hideCtaButton;

  return (
    <>
      <UitkCard>
        <UitkSpacing margin={{ blockend: "three" }} padding={{ inline: "six", block: "six" }}>
          <UitkCardContentSection>
            <Viewport>
              <ViewSmall>
                <>
                  <UitkSpacing margin={{ blockend: "three" }}>
                    <div data-stid="content-hotel-title">
                      <WrappedSummaryHeader context={context} />
                    </div>
                  </UitkSpacing>
                  <UitkSpacing margin={{ blockend: "six" }}>
                    <div>
                      <WrappedStickyBookBar
                        align="start"
                        context={context}
                        clickTargetRef="startDate"
                        isSticky={false}
                        hideCtaButton={hideCtaButton}
                      />
                    </div>
                  </UitkSpacing>
                  <UitkSpacing margin={{ blockend: "three" }}>
                    <WrappedPropertyReviews
                      context={context}
                      onClickReviews={reviewsDialogActions.openDialog}
                      {...additionalPropertyReviewsProps}
                    />
                  </UitkSpacing>
                  <UitkSpacing margin={{ blockend: "six" }}>
                    <WrappedPropertyLocationHighlights context={context} />
                  </UitkSpacing>
                  <UitkSpacing padding={{ blockend: "three" }}>
                    <WrapperPropertySummaryAmenities context={context} />
                  </UitkSpacing>
                  <div data-testid="map-image-link">
                    <WrappedSummaryMapTrigger
                      context={context}
                      onClickMap={mapDialogActions.openDialog}
                      aspectRatio={UitkFigureAspectRatioType.R21_9}
                    />
                  </div>
                </>
              </ViewSmall>
              <ViewMedium>
                <UitkLayoutGrid columns={12} space="three">
                  <UitkLayoutGridItem colSpan={8}>
                    <div>
                      <UitkSpacing padding={{ blockend: "six" }}>
                        <div data-stid="content-hotel-title">
                          <WrappedSummaryHeader context={context} />
                        </div>
                      </UitkSpacing>
                      <UitkSpacing padding={{ blockend: "four" }}>
                        <WrappedPropertyReviews
                          context={context}
                          onClickReviews={reviewsDialogActions.openDialog}
                          {...additionalPropertyReviewsProps}
                        />
                      </UitkSpacing>
                      <UitkSpacing padding={{ blockend: "six" }}>
                        <WrappedPropertyLocationHighlights context={context} />
                      </UitkSpacing>
                      <UitkSpacing padding={{ blockend: "three" }}>
                        <WrapperPropertySummaryAmenities context={context} />
                      </UitkSpacing>
                    </div>
                  </UitkLayoutGridItem>
                  <UitkLayoutGridItem colSpan={4}>
                    <div>
                      <UitkSpacing padding={{ blockend: "six" }}>
                        <div>
                          <WrappedStickyBookBar
                            context={context}
                            clickTargetRef="startDate"
                            isSticky={false}
                            hideCtaButton={hideCtaButton}
                          />
                        </div>
                      </UitkSpacing>
                      <div data-stid="map-image-link">
                        <WrappedSummaryMapTrigger
                          context={context}
                          onClickMap={mapDialogActions.openDialog}
                          aspectRatio={UitkFigureAspectRatioType.R16_9}
                        />
                      </div>
                    </div>
                  </UitkLayoutGridItem>
                </UitkLayoutGrid>
              </ViewMedium>
            </Viewport>
            <UitkSpacing margin={{ blockend: "three" }}>
              <div>
                <WrapperPropertyRelevantAmenities context={context} />
              </div>
            </UitkSpacing>
          </UitkCardContentSection>
        </UitkSpacing>
      </UitkCard>
      <ReviewsDialogComponent>
        <PropertyFilteredReviewsDialog context={context} onClose={reviewsDialogActions.closeDialog} />
      </ReviewsDialogComponent>
      <MapDialogComponent>
        <WrappedPropertyMap context={context} onClose={mapDialogActions.closeDialog} />
      </MapDialogComponent>
    </>
  );
});
export default PropertySummaryLocationHighlights;
